/**
 * localStorage handoff for prefilling the case (narrative) form from the Search page.
 *
 * When the serialized `form_*` query string is too long to travel in a URL
 * (CloudFront rejects request URLs over 8,192 bytes with a 414), the payload is
 * stored here under a short random key and only `?form_ref=<key>` goes in the URL.
 * localStorage is shared across same-origin tabs, so the value survives the
 * `window.open` hop to /narratives/new.
 *
 * Lifecycle: localStorage is only the cross-tab transport. On first read the
 * draft is moved into sessionStorage, which is per-tab: refreshes keep working
 * (sessionStorage survives reloads), while closing the tab auto-destroys it —
 * so nothing lingers after the tab is gone. After successful case creation the
 * draft is replaced with a "used" tombstone, so revisiting the stale URL shows
 * a clean blank form instead of re-prefilling. Expired localStorage entries
 * (never picked up, e.g. blocked popup) are purged on every write as a backstop.
 */

const STORAGE_PREFIX = 'osavul:caseDraft:';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface StoredCaseDraft {
  params?: string;
  used?: boolean;
  createdAt: number;
}

export type CaseDraftResult =
  | { status: 'found'; params: string }
  | { status: 'used' }
  | { status: 'missing' };

function purgeExpiredCaseDrafts(): void {
  const now = Date.now();
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '') as StoredCaseDraft;
      if (typeof stored?.createdAt !== 'number' || now - stored.createdAt > DRAFT_TTL_MS) {
        keysToRemove.push(key);
      }
    } catch {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

function generateDraftKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Stores a serialized form params string and returns the key to put in the URL,
 * or null if localStorage is unavailable (private mode, quota, disabled).
 */
export function storeCaseDraft(params: string): string | null {
  try {
    purgeExpiredCaseDrafts();
    const key = generateDraftKey();
    const draft: StoredCaseDraft = { params, createdAt: Date.now() };
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(draft));
    return key;
  } catch {
    return null;
  }
}

function parseDraft(raw: string | null): StoredCaseDraft | null {
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as StoredCaseDraft;
    if (stored?.used === true) return stored;
    if (typeof stored?.params === 'string') return stored;
    return null;
  } catch {
    return null;
  }
}

function toResult(draft: StoredCaseDraft): CaseDraftResult {
  if (draft.used === true) return { status: 'used' };
  return { status: 'found', params: draft.params as string };
}

function isExpired(draft: StoredCaseDraft): boolean {
  return typeof draft.createdAt !== 'number' || Date.now() - draft.createdAt > DRAFT_TTL_MS;
}

/**
 * Reads a stored draft by key. On the first read in a tab the draft is moved
 * from localStorage into sessionStorage (see lifecycle above). The 24h TTL is
 * enforced here too, so an expired entry reads as 'missing' even if no write
 * has purged it yet.
 * - 'found': the draft exists and the case has not been created yet
 * - 'used': a case was already created from this draft
 * - 'missing': expired, malformed, or storage unavailable
 */
export function readCaseDraft(key: string): CaseDraftResult {
  const storageKey = STORAGE_PREFIX + key;

  // This tab already picked the draft up — refreshes and Back land here.
  try {
    const sessionDraft = parseDraft(sessionStorage.getItem(storageKey));
    if (sessionDraft !== null) {
      if (isExpired(sessionDraft)) {
        sessionStorage.removeItem(storageKey);
        return { status: 'missing' };
      }
      return toResult(sessionDraft);
    }
  } catch {
    // sessionStorage unavailable — fall through to localStorage
  }

  try {
    const raw = localStorage.getItem(storageKey);
    const draft = parseDraft(raw);
    if (draft === null || raw === null) {
      return { status: 'missing' };
    }
    if (isExpired(draft)) {
      localStorage.removeItem(storageKey);
      return { status: 'missing' };
    }
    try {
      // Move to per-tab storage so the entry dies with the tab.
      sessionStorage.setItem(storageKey, raw);
      localStorage.removeItem(storageKey);
    } catch {
      // sessionStorage unavailable (private mode/quota) — keep the
      // localStorage copy so a refresh still works; TTL purge cleans it up.
    }
    return toResult(draft);
  } catch {
    return { status: 'missing' };
  }
}

/** Removes every used tombstone in the given storage except `keepStorageKey`. */
function removeOtherUsedTombstones(storage: Storage, keepStorageKey: string): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX) || key === keepStorageKey) continue;
    try {
      const stored = JSON.parse(storage.getItem(key) || '') as StoredCaseDraft;
      if (stored?.used === true) {
        keysToRemove.push(key);
      }
    } catch {
      // malformed entries are handled by the TTL purge / read path
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key));
}

/**
 * Marks a draft as used after successful case creation. Subsequent reads
 * return 'used' so the form renders blank instead of re-prefilling (or
 * showing an "expired" warning) when the stale URL is revisited.
 *
 * Only the latest tombstone is kept: older used entries are swept out here,
 * so repeated create-from-search rounds in one tab don't accumulate them.
 * Unconsumed drafts are left alone — they belong to abandoned form visits
 * still reachable through this tab's history.
 */
export function markCaseDraftUsed(key: string): void {
  const storageKey = STORAGE_PREFIX + key;
  const tombstone: StoredCaseDraft = { used: true, createdAt: Date.now() };
  const raw = JSON.stringify(tombstone);

  let sessionOk = false;
  try {
    sessionStorage.setItem(storageKey, raw);
    removeOtherUsedTombstones(sessionStorage, storageKey);
    sessionOk = true;
  } catch {
    // sessionStorage unavailable — fall back to localStorage below
  }
  try {
    if (sessionOk) {
      // The draft normally lives in sessionStorage by now; make sure no
      // localStorage copy is left behind (sessionStorage-fallback path).
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, raw);
    }
    removeOtherUsedTombstones(localStorage, storageKey);
  } catch {
    // localStorage unavailable — the TTL purge will handle any leftover
  }
}
